import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { Align } from "../../../common/TextUtils"
import { StudentStoreSA } from "../../../react/students/StudentStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { Buttons, ButtonType } from "../../generic-sprites/Buttons"
import { CheckboxGroup } from "../../generic-sprites/CheckBoxes"
import { Rectangle } from "../../generic-sprites/Shape"
import { SpeechBubble } from "../../generic-sprites/SpeechBubble"
import { Audio } from "../../utils/Audio"
import { PhaserText } from "../../utils/PhaserText"
import { x, y } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { DrTimeAssets, DrTimeAtlases, DrTimeAudio, DrTimeImage, DrTimeTests, ExamStep } from "./DrTimeAssets"
import { DrTimePatientInfo, PatientInfo } from "./DrTimePatient"
import { DrTimeProgressStore, DrTimeStages } from "./DrTimeProgressStore"
import { PatientType } from "./DrTimeTypes"
import Game = Phaser.Game


enum ExamRoomStates {
    SYMPTOMS_START = "symptoms-start",
    SYMPTOMS = "symptoms",
    TOBACCO_USE_START = "tobacco-use-start",
    TOBACCO_USE = "tobacco-use",
    CHART_START = "chart-start",
    CHART_VISIT_1 = "chart-visit-1",
    CHART_VISIT_2 = "chart-visit-2",
    CHART_VISIT_3 = "chart-visit-3",
    RUN_TEST = "run-test",
    CHART_VISIT_TODAY = "chart-visit-today",
    DIAGNOSIS_START = "diagnosis-start",
    DIAGNOSIS = "diagnosis",
    COMPLETE = "complete"
}


export class DrTimeExamRoom {
    private readonly game: Phaser.Game
    private patientInfo: PatientInfo
    readonly stage: DrTimeStages
    private examRoomState: ExamRoomStates

    private laptopScreen: Rectangle
    private totalHeight: number
    readonly laptopScreenPadding: number
    readonly laptopScreenPaddedX: number
    readonly laptopScreenPaddedY: number
    readonly laptopScreenPaddedWidth: number

    constructor(game: Phaser.Game, patientType: PatientType) {
        if (patientType === PatientType.VAPER) {
            this.patientInfo = DrTimePatientInfo[0]
            this.stage = DrTimeStages.PATIENT_VAPER
        } else {
            this.patientInfo = DrTimePatientInfo[1]
            this.stage = DrTimeStages.PATIENT_SMOKER
        }

        this.game = game
        const group = this.game.add.group()
        const background = new BackgroundSprite(game, DrTimeImage.BG_EXAM)
        group.add(background)
        Sprites.add(this.game, this.patientInfo.image, {x: x(0.056), y: y(0.096), group})

        this.laptopScreen = new Rectangle(this.game, x(0.43), y(0.37), x(0.49), y(0.43), {group, lineWidth: 0})
        this.laptopScreenPadding = x(.02)
        this.laptopScreenPaddedX = this.laptopScreen.graphics.x + this.laptopScreenPadding
        this.laptopScreenPaddedY = this.laptopScreen.graphics.y + this.laptopScreenPadding
        this.laptopScreenPaddedWidth = (this.laptopScreen.graphics.width - (this.laptopScreenPadding * 2)) / x(1)

        const screenGroup = this.game.add.group()
        const loadingText = PhaserText.create(this.game, "Loading", {group: screenGroup, align: Align.CENTER, color: COLOR.white})
        Sprites.centerTextInSprite(this.laptopScreen.invisibleSprite, loadingText, true)
        loadingText.y -= loadingText.height
        const loading = Sprites.add(this.game, DrTimeAtlases.LOADING, {group: screenGroup})
        Sprites.centerSpriteInSprite(this.laptopScreen.invisibleSprite, loading)
        loading.animations.add(DrTimeAtlases.LOADING, undefined, 45)
        Sprites.playAnimationNTimes(loading, DrTimeAtlases.LOADING, 3, () => {
            screenGroup.destroy()
            /////
            //
            // Set examRoomState something other than ExamRoomStates.SYMPTOMS_START to start at any point in the exam room
            //
            /////
            this.examRoomState = ExamRoomStates.SYMPTOMS_START
            // this.examRoomState = ExamRoomStates.SYMPTOMS
            // this.examRoomState = ExamRoomStates.TOBACCO_USE_START
            // this.examRoomState = ExamRoomStates.TOBACCO_USE
            // this.examRoomState = ExamRoomStates.CHART_START
            // this.examRoomState = ExamRoomStates.CHART_VISIT_1
            // this.examRoomState = ExamRoomStates.CHART_VISIT_2
            // this.examRoomState = ExamRoomStates.CHART_VISIT_3
            // this.examRoomState = ExamRoomStates.RUN_TEST
            // this.examRoomState = ExamRoomStates.CHART_VISIT_TODAY
            // this.examRoomState = ExamRoomStates.DIAGNOSIS_START
            // this.examRoomState = ExamRoomStates.DIAGNOSIS
            // this.examRoomState = ExamRoomStates.COMPLETE
            this.progressToNextPart()
        })
    }

    // Exam room progression
    progressToNextPart = () => {
        switch (this.examRoomState) {
            case ExamRoomStates.SYMPTOMS_START:
                this.examRoomState = ExamRoomStates.SYMPTOMS
                this.startSymptoms()
                break
            case ExamRoomStates.SYMPTOMS:
                this.examRoomState = ExamRoomStates.TOBACCO_USE_START
                this.askAboutSymptoms()
                break
            case ExamRoomStates.TOBACCO_USE_START:
                this.examRoomState = ExamRoomStates.TOBACCO_USE
                this.startTobaccoUse()
                break
            case ExamRoomStates.TOBACCO_USE:
                this.examRoomState = ExamRoomStates.CHART_START
                this.askAboutTobaccoUse()
                break
            case ExamRoomStates.CHART_START:
                this.examRoomState = ExamRoomStates.CHART_VISIT_1
                this.reviewChartStart()
                break
            case ExamRoomStates.CHART_VISIT_1:
                this.examRoomState = ExamRoomStates.CHART_VISIT_2
                this.reviewChartVisit(0)
                break
            case ExamRoomStates.CHART_VISIT_2:
                this.examRoomState = ExamRoomStates.CHART_VISIT_3
                this.reviewChartVisit(1)
                break
            case ExamRoomStates.CHART_VISIT_3:
                this.examRoomState = ExamRoomStates.RUN_TEST
                this.reviewChartVisit(2)
                break
            case ExamRoomStates.RUN_TEST:
                this.examRoomState = ExamRoomStates.CHART_VISIT_TODAY
                this.runTest()
                break
            case ExamRoomStates.CHART_VISIT_TODAY:
                this.examRoomState = ExamRoomStates.DIAGNOSIS_START
                this.reviewChartVisit(3)
                break
            case ExamRoomStates.DIAGNOSIS_START:
                this.examRoomState = ExamRoomStates.DIAGNOSIS
                this.startDiagnosis()
                break
            case ExamRoomStates.DIAGNOSIS:
                this.examRoomState = ExamRoomStates.COMPLETE
                this.getDiagnosis()
                break
            case ExamRoomStates.COMPLETE:
                this.completePatientInterview()
                break
            default:
                throw new Error(`Unknown ActivityState: ${this.examRoomState}`)
        }

        log.info('Starting exam room state', this.examRoomState, 'with patient:', this.patientInfo.type)
    }

    startSymptoms = () => {
        const screenGroup = this.game.add.group()
        const startX = x(0.5)
        const startY = y(0.5)
        const padY = y(0.01)

        const patientText = PhaserText.create(this.game, "Patient: ", {x: startX, y: startY, group: screenGroup, weight: "bold", color: COLOR.white})
        const patientName = PhaserText.create(this.game, this.patientInfo.name, {
            x: startX + patientText.width,
            y: startY,
            group: screenGroup,
            color: COLOR.yellow
        })
        const assistantY = patientName.y + patientName.height + padY
        const assistantText = PhaserText.create(this.game, "Assistant: ", {
            x: startX,
            y: assistantY,
            group: screenGroup,
            weight: "bold",
            color: COLOR.white
        })
        PhaserText.create(this.game, StudentStoreSA.instance.student.screenname, {
            x: startX + assistantText.width,
            y: assistantY,
            group: screenGroup,
            color: COLOR.yellow
        })

        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.ASK_SYMPTOMS, this.patientInfo.type), {
            onStop: () => symptomButton.enable()
        })

        const symptomButton = Buttons.create(this.game, "What are your symptoms?",
            () => {
                screenGroup.destroy()
                this.progressToNextPart()
            }, {
                enabled: false, width: 0.30
            }
        )
        symptomButton.group.centerX = this.laptopScreen.invisibleSprite.centerX
        symptomButton.group.y = this.laptopScreen.invisibleSprite.bottom - symptomButton.group.height - padY - y(.02)
        screenGroup.add(symptomButton.group)
    }

    askAboutSymptoms = () => {
        const speechBubbleGroup = this.game.add.group()
        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.PATIENT_RESPONSE_SYMPTOMS, this.patientInfo.type), {
            onStop: () => {
                const clickSymptomsAudio = this.game.add.audio(DrTimeAssets.findExamStepAudio(ExamStep.CLICK_SYMPTOMS, this.patientInfo.type))
                clickSymptomsAudio.play()
                // pull up symptoms laptop screen
                const screenGroup = this.game.add.group()
                PhaserText.create(this.game, "Click on the three symptoms that this patient has.",
                    {fontPx: 24, x: this.laptopScreenPaddedX, y: this.laptopScreenPaddedY, wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                        color: COLOR.white }
                )
                const radioGroup = new CheckboxGroup(this.game, this.laptopScreenPaddedX, this.laptopScreenPaddedY + y(.1),
                    this.patientInfo.symptomChoices,
                    (isCorrect) => {
                        if (!isCorrect) {
                            Audio.add(this.game, DrTimeAudio.TRY_AGAIN)
                        }
                    },
                    () => {
                        nextButton.enable()

                    },
                    true, false, 4, x(.25)
                )
                screenGroup.add(radioGroup.group)
                const nextButton = this.makeNextButton(screenGroup, () => speechBubbleGroup.destroy())
            }
        })
        SpeechBubble.create(this.game, {text: this.patientInfo.symptoms, group: speechBubbleGroup, x: x(0.24), y: y(0.02), width: 0.6})
    }

    startTobaccoUse = () => {
        const screenGroup = this.game.add.group()

        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.ASK_TOBACCO, this.patientInfo.type), {
            onStop: () => symptomButton.enable()
        })

        const symptomButton = Buttons.create(this.game, "Do you use tobacco?",
            () => {
                screenGroup.destroy()
                this.progressToNextPart()
            }, {
                enabled: false, width: 0.28
            }
        )
        symptomButton.group.centerX = this.laptopScreen.invisibleSprite.centerX
        symptomButton.group.centerY = this.laptopScreen.invisibleSprite.centerY

        screenGroup.add(symptomButton.group)
    }

    askAboutTobaccoUse = () => {
        const speechBubbleGroup = this.game.add.group()
        const checkboxGroupsComplete = []
        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.PATIENT_RESPONSE_SMOKING_HISTORY, this.patientInfo.type), {
            onStop: () => {
                const clickSymptomsAudio = this.game.add.audio(DrTimeAssets.findExamStepAudio(ExamStep.RECORD_HISTORY, this.patientInfo.type))
                clickSymptomsAudio.play()
                // pull up symptoms laptop screen
                const screenGroup = this.game.add.group()
                PhaserText.create(this.game, "Click on the boxes to record the patient’s tobacco use.",
                    {fontPx: 18, x: this.laptopScreenPaddedX, y: this.laptopScreenPaddedY, wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                        color: COLOR.white }
                )

                // Checkboxes
                const checkAllCorrect = (groupIndex: number): void => {
                    if (checkboxGroupsComplete.indexOf(groupIndex) == -1) {
                        checkboxGroupsComplete.push(groupIndex)
                    }
                    if (checkboxGroupsComplete.length == 3) {
                        nextButton.enable()
                    }
                }

                const makeTobaccoUseCheckBoxGroup = (checkboxGroupText, checkBoxGroupTextExtraY: number, extraCheckBoxX: number, extraCheckBoxY: number, choices, groupIndex, horizontal, checkboxWrapLength, checkboxWrapDistance): CheckboxGroup => {
                    PhaserText.create(this.game,checkboxGroupText,
                        {fontPx: 18, x: this.laptopScreenPaddedX, y: this.laptopScreenPaddedY + checkBoxGroupTextExtraY, wordWrapWidth: (this.laptopScreenPaddedWidth / 2 - x(.00001)) ,  group: screenGroup,
                            color: COLOR.white }
                    )
                    return new CheckboxGroup(this.game, this.laptopScreenPaddedX + extraCheckBoxX, this.laptopScreenPaddedY + extraCheckBoxY,
                        choices,
                        (isCorrect) => {
                            if (!isCorrect) {
                                Audio.add(this.game, DrTimeAudio.TRY_AGAIN)
                            }
                        },
                        () => {
                            checkAllCorrect(groupIndex)
                        },
                        true, horizontal, checkboxWrapLength, checkboxWrapDistance
                    )
                }

                const useTobaccoCheckboxGroup = makeTobaccoUseCheckBoxGroup(this.patientInfo.tobaccoUseChoices.useTobaccoChoices.text, y(.11), x(.2), y(.1), this.patientInfo.tobaccoUseChoices.useTobaccoChoices.choices, 0, true, 2, x(.2))
                const yearsUsedTobaccoCheckboxGroup = makeTobaccoUseCheckBoxGroup(this.patientInfo.tobaccoUseChoices.yearsUsedTobaccoChoices.text, y(.19), x(.15), y(.18), this.patientInfo.tobaccoUseChoices.yearsUsedTobaccoChoices.choices, 1, true, 3, x(.15))
                const timesUsedTobaccoPerDayCheckboxGroup = makeTobaccoUseCheckBoxGroup(this.patientInfo.tobaccoUseChoices.timesUsedTobaccoPerDayChoices.text, y(.27), x(.19), y(.26), this.patientInfo.tobaccoUseChoices.timesUsedTobaccoPerDayChoices.choices, 2, false, 2, x(.25))

                screenGroup.add(useTobaccoCheckboxGroup.group)
                screenGroup.add(yearsUsedTobaccoCheckboxGroup.group)
                screenGroup.add(timesUsedTobaccoPerDayCheckboxGroup.group)

                const nextButton = this.makeNextButton(screenGroup, () => speechBubbleGroup.destroy())
            }
        })
        SpeechBubble.create(this.game, {text: this.patientInfo.tobaccoUse, group: speechBubbleGroup, x: x(0.25), y: y(0.06), width: 0.41})
    }

    reviewChartStart = () => {
        const screenGroup = this.game.add.group()
        const padY = y(0.01)

        PhaserText.create(this.game, "Check the patient’s chart to see which symptoms were present at previous visits.",
            {fontPx: 18, x: this.laptopScreenPaddedX, y: this.laptopScreenPaddedY, wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white }
        )

        PhaserText.create(this.game, `${ this.patientInfo.chartData[0].visitTitle }:`,
            {fontPx: 18, x: this.laptopScreenPaddedX + x(.1), y: this.laptopScreenPaddedY + y(.11), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white, weight: "bold" }
        )
        PhaserText.create(this.game, `${ this.patientInfo.chartData[0].dateText }`,
            {fontPx: 18, x: this.laptopScreenPaddedX + x(.2), y: this.laptopScreenPaddedY + y(.11), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white }
        )

        PhaserText.create(this.game, `${ this.patientInfo.chartData[1].visitTitle }:`,
            {fontPx: 18, x: this.laptopScreenPaddedX + x(.1), y: this.laptopScreenPaddedY + y(.18), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white, weight: "bold" }
        )
        PhaserText.create(this.game, `${ this.patientInfo.chartData[1].dateText }`,
            {fontPx: 18, x: this.laptopScreenPaddedX + x(.2), y: this.laptopScreenPaddedY + y(.18), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white }
        )

        PhaserText.create(this.game, `${ this.patientInfo.chartData[2].visitTitle }:`,
            {fontPx: 18, x: this.laptopScreenPaddedX + x(.1), y: this.laptopScreenPaddedY + y(.25), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white, weight: "bold" }
        )
        PhaserText.create(this.game, `${ this.patientInfo.chartData[2].dateText }`,
            {fontPx: 18, x: this.laptopScreenPaddedX + x(.2), y: this.laptopScreenPaddedY + y(.25), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white }
        )

        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.CHECK_CHART, this.patientInfo.type), {
            onStop: () => symptomButton.enable()
        })

        const symptomButton = Buttons.create(this.game, "Review Chart",
            () => {
                screenGroup.destroy()
                this.progressToNextPart()
            }, {
                enabled: false, width: 0.28
            }
        )
        symptomButton.group.centerX = this.laptopScreen.invisibleSprite.centerX
        symptomButton.group.y = this.laptopScreen.invisibleSprite.bottom - symptomButton.group.height - padY - y(.02)
        screenGroup.add(symptomButton.group)
    }

    reviewChartVisit = (visit: number) => {
        const screenGroup = this.game.add.group()
        const visitData = this.patientInfo.chartData[visit]
        const timer = this.game.time.create()

        const visitTitleText = PhaserText.create(this.game, visitData.visitTitle,
            {fontPx: 16, x: this.laptopScreenPaddedX + x(.1), y: this.laptopScreenPaddedY , wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white, weight: "bold" }
        )
        const visitDateText = PhaserText.create(this.game, visitData.dateText,
            {fontPx: 16, x: this.laptopScreenPaddedX + x(.08), y: this.laptopScreenPaddedY + y(.04), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white }
        )
        visitTitleText.centerX = this.laptopScreenPaddedX + x(.12)
        visitDateText.centerX = this.laptopScreenPaddedX + x(.12)

        Sprites.add(this.game, visitData.image, {
            x: this.laptopScreenPaddedX, y: this.laptopScreenPaddedY + x(.08), scale: .7, group: screenGroup
        })

        const nextButton = this.makeNextButton(screenGroup)

        Audio.add(this.game, visitData.audio, {
            onPlay: () => timer.start(),
            onStop: () => nextButton.enable()
        })

        this.totalHeight = 0
        const chartTextGroups = visitData.notes.map((textArray) => {
            const chartTextGroup = []
            textArray.forEach((text) => {
                // Use custom wrapping rather than PhaserTest.wordWrapWidth so that we can get indentation of bullets
                const phaserText = PhaserText.create(this.game, text, {color: COLOR.white, x: this.laptopScreenPaddedX + x(.26),
                    y: this.laptopScreenPaddedY + this.totalHeight, fontPx: 16, group: screenGroup})
                this.totalHeight += phaserText.height
                phaserText.alpha = 0
                chartTextGroup.push(phaserText)
            })
            return chartTextGroup
        })

        // add timers to each step
        visitData.noteStepTimes.forEach((time, idx) => {
            timer.add(time * 1000, () => {
                chartTextGroups[idx].forEach((text) => {
                    text.alpha = 1
                })
            })
        })
    }

    runTest = () => {
        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.RUN_TEST, this.patientInfo.type), {
            onStop: () => {
                makeTestSpriteButtons(this.game, this.patientInfo.type, this.patientInfo.correctTest)
            }
        })
        // pull up symptoms laptop screen
        const screenGroup = this.game.add.group()
        const imageScale = .13

        const positions = [
            [x(0), y(0)], [x(.17), y(.02)], [x(.3), y(.01)],
            [x(.06), y(.22)], [x(.26), y(.2)]
        ]
        const testSprites: Array<Phaser.Sprite> = []
        Object.values(DrTimeTests).forEach((test, idx) => {
            const sprite = Sprites.add(this.game, test, {x: this.laptopScreenPaddedX + positions[idx][0], y: this.laptopScreenPaddedY + positions[idx][1], scale: imageScale, group: screenGroup})
            testSprites.push(sprite)
        })

        const makeTestSpriteButtons = (game: Game, patientType: PatientType, correctTest: DrTimeTests): void => {
            testSprites.forEach((testSprite) => {
                Buttons.makeSpriteButton(testSprite, () => {
                    if (testSprite.key == correctTest) {
                        selectCorrectTest(game, testSprite, patientType)
                    } else {
                        selectIncorrectTest(game, testSprite)
                    }
                })
            })
        }

        const selectCorrectTest = (game: Game, sprite: Phaser.Sprite, patientType: PatientType): void => {
            const check = Sprites.add(game, DrTimeImage.CHECKMARK, {scale: .18, group: screenGroup})
            Sprites.centerSpriteInSprite(sprite, check)
            testSprites.forEach((testSprite) => {
                Buttons.undoSpriteButton(testSprite)
            })
            Audio.add(game, DrTimeAssets.findExamStepAudio(ExamStep.THATS_RIGHT, patientType), {
                onStop: () => {
                    nextButton.enable()
                }
            })
        }

        const selectIncorrectTest = (game: Game, sprite: Phaser.Sprite): void => {
            const cross = Sprites.add(game, DrTimeImage.CROSS, {scale: .15, group: screenGroup})
            Sprites.centerSpriteInSprite(sprite, cross)
            Buttons.undoSpriteButton(sprite)
            Audio.add(game, DrTimeAudio.TRY_AGAIN)
        }

        const nextButton = this.makeNextButton(screenGroup, () => screenGroup.destroy())
    }

    addChartText = (screenGroup: Phaser.Group, text: string) => {
        const phaserText = PhaserText.create(this.game, text, {color: COLOR.white, x: this.laptopScreenPaddedX,
            y: this.laptopScreenPaddedY + y(.1) + this.totalHeight, fontPx: 16, wordWrapWidth: this.laptopScreenPaddedWidth, group: screenGroup})
        this.totalHeight += phaserText.height
        phaserText.alpha = 0
        return phaserText
    }

    startDiagnosis = () => {
        const screenGroup = this.game.add.group()
        const timer = this.game.time.create()

        PhaserText.create(this.game, "Review the patient’s symptoms and get a medical opinion (diagnosis).",
            {fontPx: 16, x: this.laptopScreenPaddedX, y: this.laptopScreenPaddedY, wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white }
        )

        const requestDiagnosisButton = Buttons.create(this.game, "Request Diagnosis",
            () => {
                screenGroup.destroy()
                this.progressToNextPart()
            }, {
                enabled: false, width: 0.28, y: this.laptopScreenPaddedY + y(.32), group: screenGroup
            }
        )
        requestDiagnosisButton.button.centerX = this.laptopScreen.invisibleSprite.centerX
        requestDiagnosisButton.text.centerX = this.laptopScreen.invisibleSprite.centerX

        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.DIAGNOSIS_2, this.patientInfo.type), {
            onPlay: () => timer.start(),
            onStop: () => requestDiagnosisButton.enable()
        })

        this.totalHeight = 0
        const chartTexts = this.patientInfo.diagnosis.symptoms.map((text) => {
            return this.addChartText(screenGroup, text)
        })

        // add timers to each step
        this.patientInfo.diagnosis.symptomsStepTimes.forEach((time, idx) => {
            timer.add(time * 1000, () => {
                chartTexts[idx].alpha = 1
            })
        })
    }

    getDiagnosis = () => {
        const screenGroup = this.game.add.group()
        const timer = this.game.time.create()

        const titleText = PhaserText.create(this.game, "Diagnosis:",
            {fontPx: 16, y: this.laptopScreenPaddedY , wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white, weight: "bold" }
        )
        titleText.centerX = this.laptopScreen.invisibleSprite.centerX
        const diagnosisText = PhaserText.create(this.game, this.patientInfo.diagnosis.diagnosisText,
            {fontPx: 16, y: this.laptopScreenPaddedY + y(.04), wordWrapWidth: this.laptopScreenPaddedWidth,  group: screenGroup,
                color: COLOR.white }
        )
        diagnosisText.centerX = this.laptopScreen.invisibleSprite.centerX

        const nextButton = this.makeNextButton(screenGroup)

        Audio.add(this.game, DrTimeAssets.findExamStepAudio(ExamStep.DIAGNOSIS_3, this.patientInfo.type), {
            onPlay: () => timer.start(),
            onStop: () => nextButton.enable()
        })

        this.totalHeight = 0
        const chartTexts = this.patientInfo.diagnosis.diagnosisNotes.map((text) => {
            return this.addChartText(screenGroup, text)
        })

        // add timers to each step
        this.patientInfo.diagnosis.diagnosisNotesStepTimes.forEach((time, idx) => {
            timer.add(time * 1000, () => {
                chartTexts[idx].alpha = 1
            })
        })
    }

    completePatientInterview = () => {
        DrTimeProgressStore.instance.addStage(this.stage)
        this.game.state.start(ActivityKey.DR_TIME)
    }

    makeNextButton = (group: Phaser.Group, extraOnClick?: () => void) => {
        return Buttons.create(
            this.game,
            "Next",
            () => {
                group.destroy()
                if (extraOnClick) {
                    extraOnClick()
                }
                this.progressToNextPart()
            },
            {x: this.laptopScreenPaddedX + x(.39), y: this.laptopScreenPaddedY + y(.33), width: 0.07, enabled: false, group: group, buttonType: ButtonType.SECONDARY}
        )
    }

}